import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";
import { API, imageSrc } from "@/lib/api";

export default function GalleryStrip() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 sm:py-28 border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-12 flex flex-wrap items-end justify-between gap-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-disco-teal text-xs font-semibold tracking-[0.35em] uppercase mb-4 flex items-center gap-2">
            <Instagram size={14} />
            Fresh Off the Gram
          </p>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl text-[#F8F8F8]">
            Life on <span className="neon-yellow">the cart</span>
          </h2>
        </motion.div>
        <motion.a
          href="https://instagram.com/discohotdogs"
          target="_blank"
          rel="noreferrer"
          data-testid="gallery-follow-link"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 rounded-full border border-disco-lavender/60 text-disco-lavender text-sm font-semibold px-6 py-2.5 transition-all duration-300 hover:bg-disco-lavender/10 hover:border-disco-lavender active:scale-95"
        >
          Follow @discohotdogs
          <ArrowUpRight size={15} />
        </motion.a>
      </div>

      {items.length > 0 && (
        <div
          data-testid="gallery-strip"
          className="flex gap-4 overflow-x-auto px-5 sm:px-8 pb-4 snap-x snap-mandatory [scrollbar-width:thin] [scrollbar-color:#B57EDC_transparent]"
        >
          {items.map((item, i) => (
            <motion.a
              key={item.id}
              href={item.link || "https://instagram.com/discohotdogs"}
              target="_blank"
              rel="noreferrer"
              data-testid={`gallery-item-${item.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 6) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className="group relative shrink-0 w-56 sm:w-64 aspect-square snap-start overflow-hidden border border-white/10"
            >
              <img
                src={imageSrc(item.image)}
                alt={item.caption || "Disco Dogs on Instagram"}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors duration-300" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4">
                <Instagram size={22} className="text-disco-yellow" />
                {item.caption && (
                  <p className="text-xs text-zinc-100 text-center font-light">{item.caption}</p>
                )}
              </div>
            </motion.a>
          ))}
        </div>
      )}
    </section>
  );
}
